import 'css/src/components/common/AnnouncementCard';

import React from 'react';

import TimeAgo from 'js/src/components/common/ui/TimeAgo';
import { trackAnnouncementCtaClick } from 'js/src/data/announcements';
import type { Announcement } from 'js/src/data/announcements/announcement-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';

function getYouTubeEmbedUrl(url: string): string {
    if (!url) return url;
    if (url.includes('/embed/')) return url;
    const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (watchMatch) {
        return `https://www.youtube.com/embed/${watchMatch[1]}`;
    }
    return url;
}

type AnnouncementCardProps = {
    announcement: ImmutableMap<Announcement>;
    onCtaClick?: () => void;
    titleTag?: 'h2' | 'h3' | 'h4';
};

export const AnnouncementCard: React.FC<AnnouncementCardProps> = React.memo(
    function AnnouncementCard({ announcement, onCtaClick, titleTag: TitleTag = 'h4' }) {
        const dispatch = useAppDispatch();
        const id = announcement.get('id');
        const title = announcement.get('title');
        const body = announcement.get('body');
        const imageUrl = announcement.get('image_url');
        const videoUrl = announcement.get('video_url');
        const ctaText = announcement.get('cta_text');
        const ctaUrl = announcement.get('cta_url');
        const category = announcement.get('category');
        const publishDate = announcement.get('publish_date');
        const isPreview = announcement.get('is_preview');

        const handleCtaClick = React.useCallback(() => {
            dispatch(trackAnnouncementCtaClick(id));
            if (ctaUrl) {
                window.open(ctaUrl, '_blank', 'noopener,noreferrer');
            }
            onCtaClick?.();
        }, [dispatch, id, ctaUrl, onCtaClick]);

        return (
            <div className="announcement-card">
                {isPreview && (
                    <div className="announcement-card__preview-badge">
                        <i className="material-symbols-outlined me-l5-4">visibility</i>
                        Preview - Only visible to you
                    </div>
                )}

                <div className="announcement-card__header">
                    {category && (
                        <span
                            className="announcement-card__category"
                            style={{ backgroundColor: category.get('color') }}
                        >
                            {category.get('name')}
                        </span>
                    )}
                    <TimeAgo time={publishDate} className="announcement-card__date" />
                </div>

                <TitleTag className="announcement-card__title">{title}</TitleTag>

                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt=""
                        className="announcement-card__image"
                        loading="lazy"
                    />
                )}

                {videoUrl && (
                    <div className="announcement-card__video">
                        <iframe
                            src={getYouTubeEmbedUrl(videoUrl)}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                )}

                <div
                    className="announcement-card__body"
                    dangerouslySetInnerHTML={{ __html: body }}
                />

                {ctaText && ctaUrl && (
                    <button
                        type="button"
                        className="announcement-card__cta"
                        onClick={handleCtaClick}
                    >
                        {ctaText}
                    </button>
                )}
            </div>
        );
    },
);

export default AnnouncementCard;
